import { Check } from 'lucide-react';

interface SubscriptionSelectorProps {
  selectedPlan: 'starter' | 'pro' | 'premium' | null;
  onSelectPlan: (plan: 'starter' | 'pro' | 'premium') => void;
  prices: {
    starter: number;
    pro: number;
    premium: number;
  };
}

const TIER_FEATURES = {
  starter: [
    'Job Match AI analysis (5/month)',
    'Skills Tracker with progress analytics',
    'Basic AI coaching sessions',
    'Email support',
  ],
  pro: [
    'Job Match AI analysis (20/month)',
    'Advanced Skills Tracker with insights',
    'Unlimited AI coaching sessions',
    'Interview Strategy Coach access',
    'Career Development Strategist access',
    'Priority email support',
  ],
  premium: [
    'Unlimited Job Match AI analysis',
    'Premium Skills Tracker with predictions',
    'Unlimited AI coaching with all personas',
    'Executive Interview Coach access',
    'Career Development Strategist access',
    'Voice-enabled coaching sessions',
    'Priority phone & email support',
    'Early access to new features',
  ],
};

export function SubscriptionSelector({
  selectedPlan,
  onSelectPlan,
  prices,
}: SubscriptionSelectorProps) {
  const tiers = [
    {
      id: 'starter' as const,
      name: 'Starter',
      description: 'Perfect for getting started with ongoing insights',
      price: prices.starter,
      popular: false,
    },
    {
      id: 'pro' as const,
      name: 'Pro',
      description: 'Best for active job seekers and career growth',
      price: prices.pro,
      popular: true,
    },
    {
      id: 'premium' as const,
      name: 'Premium',
      description: 'Complete career intelligence platform',
      price: prices.premium,
      popular: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {tiers.map((tier) => (
        <button
          key={tier.id}
          onClick={() => onSelectPlan(tier.id)}
          className={`relative p-6 rounded-lg border-2 transition-all text-left ${
            selectedPlan === tier.id
              ? 'border-teal-500 bg-teal-900/20 shadow-lg shadow-teal-500/20'
              : 'border-slate-600 bg-slate-800/30 hover:border-slate-500'
          }`}
        >
          {tier.popular && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAR
              </span>
            </div>
          )}

          <div className="mb-4">
            <h4 className="text-xl font-bold text-white mb-1">{tier.name}</h4>
            <p className="text-sm text-gray-400">{tier.description}</p>
          </div>

          <div className="mb-4">
            <span className="text-3xl font-bold text-white">${tier.price}</span>
            <span className="text-gray-400 text-sm">/month</span>
          </div>

          <ul className="space-y-2">
            {TIER_FEATURES[tier.id].map((feature, i) => (
              <li key={i} className="flex items-start text-sm text-gray-300">
                <Check
                  className={`mr-2 flex-shrink-0 mt-0.5 ${
                    selectedPlan === tier.id ? 'text-teal-400' : 'text-gray-500'
                  }`}
                  size={16}
                />
                {feature}
              </li>
            ))}
          </ul>

          {selectedPlan === tier.id && (
            <div className="mt-4 pt-4 border-t border-teal-500/30">
              <span className="text-sm font-medium text-teal-400">Selected</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
